<script>
  import { inertia, router } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AppLayout from '../../components/AppLayout.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Button from '../../components/ui/Button.svelte';
  import StatusBadge from '../../components/StatusBadge.svelte';
  import { inkSeedFor, INK_CARD_AUGS } from '../../lib/ink.js';

  let { posts = [] } = $props();

  // Deterministic per post: the same record keeps the same tear across
  // renders, but a list never shows two identical corners in a row.
  function inkAttrs(post) {
    const seed = inkSeedFor(post.id);
    return { aug: INK_CARD_AUGS[seed % INK_CARD_AUGS.length], seed };
  }

  // The whole row is already a link to the post, so Reuse can't be an <a>
  // nested inside it. Navigate manually and stop the click from also
  // following the row.
  function reuse(event, post) {
    event.preventDefault();
    event.stopPropagation();
    router.visit(`/posts/${post.id}/reuse`);
  }

  function when(post) {
    if (post.scheduled_at) return `Scheduled ${new Date(post.scheduled_at).toLocaleString()}`;
    if (post.published_at) return `Published ${new Date(post.published_at).toLocaleString()}`;
    return `Created ${new Date(post.created_at).toLocaleDateString()}`;
  }
</script>

<AppLayout title="Posts">
  <div class="flex justify-end mb-6">
    <Button href="/posts/new"><Icon icon="mdi:plus" class="mr-1" /> Compose post</Button>
  </div>

  {#if posts.length === 0}
    <Card>
      <div class="text-center py-8">
        <Icon icon="mdi:pencil-outline" class="text-4xl text-secondary-300 mx-auto mb-3" />
        <p class="text-secondary-500 mb-4">No posts yet.</p>
        <Button href="/posts/new">Compose your first post</Button>
      </div>
    </Card>
  {:else}
    <div class="flex flex-col gap-3">
      {#each posts as post}
        <a use:inertia href={`/posts/${post.id}`} data-augmented-ui={inkAttrs(post).aug} data-ink-seed={inkAttrs(post).seed} class="card !p-5 hover:border-primary-200 flex items-start justify-between gap-4">
          <div class="min-w-0 flex gap-3">
            {#if post.media_count > 0}
              <div class="flex gap-1 shrink-0">
                {#each post.media.slice(0, 3) as m}
                  <div class="relative w-12 h-12 rounded-lg overflow-hidden border border-secondary-200 bg-secondary-50">
                    {#if m.url && !post.media_expired}
                      <img src={m.url} alt="" class="w-full h-full object-cover" />
                    {:else}
                      <div class="w-full h-full flex items-center justify-center text-secondary-300">
                        <Icon icon={post.media_expired ? 'mdi:image-off-outline' : 'mdi:image-outline'} />
                      </div>
                    {/if}
                  </div>
                {/each}
                {#if post.media_count > 3}
                  <div class="w-12 h-12 rounded-lg border border-secondary-200 flex items-center justify-center text-xs text-secondary-400">
                    +{post.media_count - 3}
                  </div>
                {/if}
              </div>
            {/if}
            <div class="min-w-0">
              <p class="text-secondary-800 mb-2">{post.body}</p>
              <div class="flex items-center gap-2 text-xs text-secondary-400">
                <Icon icon="mdi:clock-outline" />
                {when(post)}
                <span class="text-secondary-300">·</span>
                {post.targets.length} {post.targets.length === 1 ? 'account' : 'accounts'}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onclick={(e) => reuse(e, post)}
              class="flex items-center gap-1 text-xs text-secondary-500 hover:text-primary-600 rounded-lg px-2 py-1 hover:bg-secondary-50"
              aria-label="Reuse this post as a new draft"
            >
              <Icon icon="mdi:content-copy" /> Reuse
            </button>
            <StatusBadge status={post.status} />
          </div>
        </a>
      {/each}
    </div>
  {/if}
</AppLayout>
